import { useEffect, useRef } from "react";
import { useEscapeLayer } from "./useEscapeLayer";

// Elements a modal can hand focus to. Mirrors the browser's tab order closely
// enough for our hand-rolled dialogs (no positive tabindex in this codebase).
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

/**
 * Focus management for hand-rolled modal dialogs (slide-overs, drawers).
 *
 * While `active` (default: as long as the dialog is mounted):
 * - on open, saves the currently focused element and moves focus into the
 *   dialog (first focusable element, else the container itself);
 * - traps Tab / Shift+Tab inside the dialog and calls `onClose` on Escape,
 *   unless a menu or select open above it takes Escape first;
 * - on close/unmount, restores focus to the previously focused element.
 *
 * Returns a ref to attach to the dialog container (the `role="dialog"` node).
 */
export function useModalFocus<T extends HTMLElement>(onClose: () => void, active = true) {
  const containerRef = useRef<T>(null);

  useEscapeLayer(onClose, active);

  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const focusables = () =>
      Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));

    const first = focusables()[0];
    // A field that took focus itself on mount (autoFocus) keeps it.
    if (container.contains(document.activeElement) && document.activeElement !== container) {
      // Focus is already where the dialog wants it.
    } else if (first) {
      first.focus();
    } else {
      container.tabIndex = -1;
      container.focus();
    }

    function handleKeyDown(e: KeyboardEvent) {
      // A select or menu inside already moved focus for this Tab.
      if (e.key !== "Tab" || e.defaultPrevented || !container) return;

      const elements = focusables();
      if (elements.length === 0) {
        // Nothing tabbable inside: keep focus pinned on the container.
        e.preventDefault();
        container.focus();
        return;
      }
      const firstElement = elements[0];
      const lastElement = elements[elements.length - 1];
      if (!firstElement || !lastElement) return;
      const current = document.activeElement;
      if (e.shiftKey) {
        if (current === firstElement || !container.contains(current)) {
          e.preventDefault();
          lastElement.focus();
        }
      } else if (current === lastElement || !container.contains(current)) {
        e.preventDefault();
        firstElement.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [active]);

  return containerRef;
}
